import { defineStore } from 'pinia'
import cmsApi from '@vue3/api/cms-api'
import authApi from '@vue3/api/auth-api'
import useAgreementStore from '@vue3/store/agreement-store'
import { emitEventToVuexStore, isIOSAppCheck, isAndroidAppCheck } from '@vue3/utils/common.js'
import { setAuthError } from '@vue3/utils/auth'
import { clearAllAgreementTimestampTokens } from '@vue3/utils/agreement'
import {
  setLocalStorageAppConfiguration, getLocalStorageAppConfiguration,
  getCondensedBannersConfigResponse, setSessionUpdatedDate, clearSessionUpdatedDate, getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem, applyTheme
} from '@vue3/utils/common'
import CommonStore from '@vue3/store/common-store'
import { ensureAndWatchFcmToken, shouldShowNotificationModal, deleteTokenFromServer, checkForFCMSupport } from '@vue3/firebase'

const authStore = defineStore('authStore', {
  state: () => ({
    isAppConfigFetched: false,
    isSessionAPIUpdateCallInProgress: false,
    isSendEmailBtnClicked: false,
    isVerificationLinkExpired: true,
    isPasswordVerificationLinkExpired: false,
    visiblePasswordStrength: false,
    isLoggedIn: false,
    isUserFetchFinished: false,
    appConfigs: null,
    userDetails: {},
    isPasswordSaved: false,
    banners: [],
    resetPasswordResponse: '',
    currentCompanyId: null,
    currentCompanyInfo: {},
    tenantList: [],
    currentTenantId: '',
    updateThemeAfterLogin: false,
    isNotificationModalVisible: false,
    isUserFetchAfterLogin: true,
    isNotificationPermissionGranted: false
  }),
  getters: {
    getResetPasswordResponse (state) {
      return state.resetPasswordResponse
    },
    getBanners (state) {
      return state.banners
    },
    getDealerId () {
      return this.currentCompanyInfo?.dealerId || ''
    },
    getUserEmailId () {
      return this.userDetails?.email
    },
    getRegistrationId () {
      return this.userDetails.id
    }
  },
  actions: {
    setIsLoggedIn (value) {
      this.isLoggedIn = value
    },
    setAppConfigFetched (value) {
      this.isAppConfigFetched = value
    },
    setAppConfig (value) {
      this.appConfigs = value
    },
    setIsUserFetchFinished (value) {
      this.isUserFetchFinished = value
    },
    setUserDetails (value) {
      this.userDetails = value
      CommonStore().setRegistrationId(value?.id)
    },
    setVisiblePasswordStrength (value) {
      this.visiblePasswordStrength = value
    },
    setVerificationLinkExpired (value) {
      this.isVerificationLinkExpired = value
    },
    setIsSendEmailBtnClicked (value) {
      this.isSendEmailBtnClicked = value
    },
    setPasswordVerificationLinkExpired (value) {
      this.isPasswordVerificationLinkExpired = value
    },
    setIsPasswordSaved (value) {
      this.isPasswordSaved = value
    },
    setResetPasswordResponse (value) {
      this.resetPasswordResponse = value
    },
    setCurrentCompanyId (value) {
      this.currentCompanyId = value
    },
    setCurrentCompanyInfo (value) {
      this.currentCompanyInfo = value
    },
    setFirstLoginApproval (value) {
      this.currentCompanyInfo = {
        ...this.currentCompanyInfo,
        firstLoginPostApproval: value
      }
    },
    setNotificationModalVisible (value) {
      this.isNotificationModalVisible = value
    },
    setCurrentTenantId (value) {
      this.currentTenantId = value
    },
    setTenantList (value) {
      this.tenantList = value
    },
    setIsNotificationPermissionGranted (value) {
      this.isNotificationPermissionGranted = value
    },
    resetUserDetails () {
      this.userDetails = {}
      this.isLoggedIn = false
      this.currentCompanyId = null
      this.currentCompanyInfo = {}
      this.tenantList = []
      this.currentTenantId = ''
      clearAllAgreementTimestampTokens()
      setAuthError(401)
      emitEventToVuexStore('PINIA_UPDATE_USER_DETAILS', {})
      emitEventToVuexStore('PINIA_UPDATE_USER_LOGGED_IN', false)
      emitEventToVuexStore('PINIA_UPDATE_CURRENT_COMPANY_ID', null)
      emitEventToVuexStore('PINIA_UPDATE_CURRENT_COMPANY_INFO', {})
      emitEventToVuexStore('PINIA_UPDATE_TENANT_LIST', [])
      emitEventToVuexStore('PINIA_UPDATE_TENANT_ID', '')
      clearSessionUpdatedDate()
    },
    getAppConfiguration ({ query, success, fail } = {}) {
      cmsApi.getConfig(query).then(response => {
        if (response.body?.code === 200) {
          if (query?.id === 'applereseller.ui.config') {
            this.appConfigs = JSON.parse(response.body?.data?.value)
            this.isAppConfigFetched = true
            success && success(this.appConfigs)
          }
        } else {
          this.isAppConfigFetched = true
          fail && fail(response.body)
        }
      }).catch(err => {
        this.isAppConfigFetched = true
        fail && fail(err)
      })
    },
    getLandingPageConfiguration ({ query, success, fail } = {}) {
      cmsApi.getConfig(query).then(response => {
        if (response.body?.code === 200) {
          success && success(JSON.parse(response.body?.data?.value))
        } else {
          fail && fail(response.body)
        }
      }).catch(err => {
        fail && fail(err)
      })
    },
    updateSession () {
      if (this.isSessionAPIUpdateCallInProgress) {
        return
      }
      this.isSessionAPIUpdateCallInProgress = true
      authApi.sessionUpdate()
        .then(() => {
          setSessionUpdatedDate()
          this.isSessionAPIUpdateCallInProgress = false
        }).catch(() => {
          this.isSessionAPIUpdateCallInProgress = false
          clearSessionUpdatedDate()
        })
    },
    logoutUser ({ success, fail } = {}) {
      const performLogout = () => {
        authApi.userLogout().then(() => {
          this.resetUserDetails()
          success && success()
          removeLocalStorageItem('theme')
          const isInternalUser = getLocalStorageItem('isInternalUser')
          if (isInternalUser) {
            removeLocalStorageItem('isInternalUser')
            location.reload()
          }
        }).catch(() => {
          fail && fail()
        })
      }

      deleteTokenFromServer({
        success: performLogout,
        fail: performLogout
      })
    },
    updateRegistrationStep (value) {
      this.currentCompanyInfo = {
        ...this.currentCompanyInfo,
        registrationNextStep: value
      }
      this.userDetails = {
        ...this.userDetails,
        companyDetails: this.currentCompanyInfo
      }
      emitEventToVuexStore('PINIA_UPDATE_USER_DETAILS', this.userDetails)
      emitEventToVuexStore('PINIA_UPDATE_CURRENT_COMPANY_INFO', this.currentCompanyInfo)
    },
    updateRegistrationStatus (value) {
      this.currentCompanyInfo = {
        ...this.currentCompanyInfo,
        companyRegistrationStatus: value
      }
      this.userDetails = {
        ...this.userDetails,
        companyDetails: this.currentCompanyInfo
      }
      emitEventToVuexStore('PINIA_UPDATE_USER_DETAILS', this.userDetails)
      emitEventToVuexStore('PINIA_UPDATE_CURRENT_COMPANY_INFO', this.currentCompanyInfo)
    },
    async handleNotificationPermission () {
      const supported = await checkForFCMSupport()
      this.isNotificationModalVisible = (supported || isIOSAppCheck || isAndroidAppCheck) ? shouldShowNotificationModal() : false
    },
    async handleGetFcmToken () {
      const supported = await checkForFCMSupport()
      if (supported || isIOSAppCheck || isAndroidAppCheck) {
        ensureAndWatchFcmToken()
      }
    },
    handleThemePreference (themeFromMemberInfo) {
      const themeFromLocalStorage = getLocalStorageItem('theme')

      const applyAndSave = (theme) => {
        const resolvedTheme = theme || 'LIGHT'
        applyTheme(resolvedTheme)
        if (theme) {
          setLocalStorageItem('theme', theme)
        }
        return resolvedTheme
      }

      if (themeFromLocalStorage && themeFromMemberInfo !== themeFromLocalStorage && this.updateThemeAfterLogin) {
        const theme = applyAndSave(themeFromLocalStorage)
        this.switchTheme({
          query: { themePreference: theme }
        })
        this.updateThemeAfterLogin = false
      } else {
        applyAndSave(themeFromMemberInfo)
      }
    },
    getUserInfo ({ success, fail, skipAgreementPopup = false } = {}) {
      authApi.getUserDetails()
        .then((res) => {
          this.userDetails = res.body.value
          this.isLoggedIn = true
          this.isUserFetchFinished = true
          const { companyDetails = null, tenantDetailsDTOS = [], theme } = this.userDetails
          this.currentCompanyId = companyDetails?.companyId || ''
          this.currentCompanyInfo = companyDetails || {}
          this.tenantList = tenantDetailsDTOS
          this.currentTenantId = companyDetails?.tenantId || ''
          this.handleThemePreference(theme)
          this.handleNotificationPermission()

          if (!skipAgreementPopup && companyDetails?.showAgreementPopup && companyDetails?.agreementId && companyDetails?.dealerId) {
            useAgreementStore().openAgreementIfNeeded({
              dealerId: companyDetails.dealerId,
              agreementId: companyDetails.agreementId
            })
          }

          if (!this.isUserFetchAfterLogin) {
            this.handleGetFcmToken()
          }
          emitEventToVuexStore('PINIA_UPDATE_USER_DETAILS', this.userDetails)
          emitEventToVuexStore('PINIA_UPDATE_CURRENT_COMPANY_ID', this.currentCompanyId)
          emitEventToVuexStore('PINIA_UPDATE_CURRENT_COMPANY_INFO', this.currentCompanyInfo)
          emitEventToVuexStore('PINIA_UPDATE_TENANT_LIST', this.tenantList)
          emitEventToVuexStore('PINIA_UPDATE_TENANT_ID', this.currentTenantId)
          emitEventToVuexStore('PINIA_UPDATE_USER_LOGGED_IN', true)
          emitEventToVuexStore('PINIA_USER_FETCH_FINISHED', true)
          emitEventToVuexStore('PINIA_AUTH_ERROR', false)
          setSessionUpdatedDate()
          setAuthError(false)
          success && success(this.userDetails)
        }).catch(err => {
          this.userDetails = {}
          this.isLoggedIn = false
          this.isUserFetchFinished = true
          this.currentCompanyId = null
          this.currentCompanyInfo = {}
          this.tenantList = []
          this.currentTenantId = ''
          setAuthError(401)
          emitEventToVuexStore('PINIA_UPDATE_USER_LOGGED_IN', false)
          emitEventToVuexStore('PINIA_USER_FETCH_FINISHED', true)
          emitEventToVuexStore('PINIA_AUTH_ERROR', false)
          emitEventToVuexStore('PINIA_UPDATE_CURRENT_COMPANY_ID', null)
          emitEventToVuexStore('PINIA_UPDATE_CURRENT_COMPANY_INFO', {})
          emitEventToVuexStore('PINIA_UPDATE_TENANT_LIST', [])
          emitEventToVuexStore('PINIA_UPDATE_TENANT_ID', '')
          fail && fail(err)
        })
    },
    verifyEmailWithId ({ data, success, fail } = {}) {
      authApi.verifyEmailWithId(data)
        .then((res) => {
          success && success(res.body)
        }).catch(err => {
          fail && fail(err)
        })
    },
    postVerifyPasswordWithId ({ data, success, fail } = {}) {
      authApi.postVerifyPasswordWithId(data)
        .then(res => {
          success && success(res.body)
        })
        .catch(err => {
          fail && fail(err)
        })
    },
    postForgotPasswordInfo ({ data, success, fail } = {}) {
      authApi.postForgotPasswordInfo(data)
        .then(res => {
          if (res.body.success) {
            success && success(res.body)
          } else {
            fail && fail(res.body)
          }
        })
        .catch(err => {
          fail && fail(err)
        })
    },
    postResetPasswordInfo ({ data, success, fail } = {}) {
      authApi.postResetPasswordInfo(data)
        .then(res => {
          success && success(res.body)
        })
        .catch(err => {
          fail && fail(err)
        })
    },
    verifyUser ({ data, success, fail } = {}) {
      authApi.userLogin(data)
        .then((res) => {
          this.updateThemeAfterLogin = true
          this.isUserFetchAfterLogin = true
          success && success(res.body)
        }).catch(err => {
          fail && fail(err)
        })
    },
    createUser ({ data, success, fail } = {}) {
      authApi.userSignup(data)
        .then((res) => {
          success && success(res.body)
        }).catch(err => {
          fail && fail(err)
        })
    },
    postVerificationEmail ({ data, success, fail } = {}) {
      authApi.sendVerificationEmail(data)
        .then(res => {
          if (res.body.success) {
            success && success(res.body)
          } else {
            fail && fail(res.body)
          }
        })
        .catch(err => {
          fail && fail(err)
        })
    },
    getBannersAndServices () {
      this.isBannersLoading = true
      cmsApi.getServicesPromotion().then(response => {
        if (response.body.code === 200) {
          const mainResponse = response.body?.data?.[0]
          const bannerConfig = getCondensedBannersConfigResponse(mainResponse?.blocks?.[0]?.components)
          this.banners = bannerConfig
          setLocalStorageAppConfiguration('appleResellerBannersConfig', JSON.stringify(bannerConfig))
        } else {
          const getBannersConfig = getLocalStorageAppConfiguration('appleResellerBannersConfig')
          if (getBannersConfig) {
            this.banners = JSON.parse(getBannersConfig)
          }
        }
        this.isBannersLoading = false
      },
      () => {
        this.isBannersLoading = false
        const getBannersConfig = getLocalStorageAppConfiguration('appleResellerBannersConfig')
        if (getBannersConfig) {
          this.banners = JSON.parse(getBannersConfig)
        }
      })
    },
    switchCompany ({ data, success, fail } = {}) {
      authApi.switchCompany(data)
        .then(res => {
          if (res.body.success) {
            success && success(res.body)

          } else {
            fail && fail(res.body)
          }
        })
        .catch(err => {
          fail && fail(err)
        })
    },
    switchTheme ({ query, success, fail } = {}) {
      authApi.switchTheme(query)
        .then(res => {
          if (res.body.success) {
            success && success(res.body)
          }
        })
        .catch(err => {
          fail && fail(err)
        })
    },
    updateNickName ({ data, selectedTenant, success, fail } = {}) {
      authApi.updateNickName(data)
        .then(res => {
          if (res.body.success) {
            const companyList = this.tenantList?.find(item => item.tenantId === selectedTenant)?.companyList || []
            const company = companyList.find(company => company.companyId === data.childCompanyId) || {}
            company.nickname = data.nickname
            success && success(res.body)
          } else {
            fail && fail(res.body)
          }
        })
        .catch(err => {
          fail && fail(err)
        })
    }
  }
})

export default authStore
